import { pdf } from "@react-pdf/renderer";
import FileSaver from "file-saver";
import React, { useState } from "react";
import { PDFDocument } from "../table/PDFDocument";
import { useFormContext } from "react-hook-form";
import { CreateBillDto } from "../model/bill/dto/CreateBillDto";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { billActions } from "../redux/bill/bill.slice";
import { AtiraToast } from "../utils/AtiraToast";
import { billSliceSelectors } from "../redux/bill/bill.selector";
import { UpdateBillDto } from "../model/bill/dto/UpdateBillDto";
import { Button } from "./Button";
import { Bill } from "../model/bill/types/Bill";

type SaveButtonProps = {
  onClose: VoidFunction;
  editMode?: boolean;
};

export const SaveButton: React.FC<SaveButtonProps> = ({
  onClose,
  editMode = false,
}) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const billsPageSize = useAppSelector(
    billSliceSelectors.selectBillsTablePageSize
  );
  const billsPage = useAppSelector(billSliceSelectors.selectBillsTablePage);
  const billNumber = useAppSelector(billSliceSelectors.selectCurrentBillNumber);

  const { getValues } = useFormContext<CreateBillDto | UpdateBillDto | Bill>();

  const onEdit = async (dto: UpdateBillDto) => {
    const payload = {
      ...dto,
      billId: dto._id,
      billDetails: (dto?.billDetails || []).filter(
        (bd) =>
          bd.qty?.length ||
          bd.code?.length ||
          bd.unitPrice?.length ||
          bd.description?.length
      ),
    } as UpdateBillDto;

    await dispatch(billActions.updateBill(payload)).unwrap();
  };

  const onCreate = async (dto: CreateBillDto) => {
    const blob = await pdf(
      <PDFDocument
        dto={{ ...dto, billNumber: (dto as any).billNumber || billNumber }}
      />
    ).toBlob();

    const bill = await dispatch(
      billActions.createBill({
        ...dto,
        invoiceDate: new Date(
          (dto as CreateBillDto).invoiceDate
        ) as unknown as string,
        billDetails: (dto as CreateBillDto).billDetails.filter(
          (bd) =>
            bd.qty?.length ||
            bd.code?.length ||
            bd.unitPrice?.length ||
            bd.description?.length
        ),
      } as CreateBillDto)
    ).unwrap();

    FileSaver.saveAs(blob, `shamautoparts-${bill.billNumber}.pdf`);
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      dto.receivedBy = dto?.receivedBy?.trim();
      dto.soldTo = dto?.soldTo?.trim();
      dto.billDetails?.forEach(
        (b) =>
          (b.amount = (Number(b?.qty || 0) * Number(b?.unitPrice || 0)).toFixed(
            2
          ))
      );

      if (editMode) {
        await onEdit(dto as UpdateBillDto);
      } else {
        await onCreate(dto as CreateBillDto);
      }

      await dispatch(billActions.getBillNumber()).unwrap();
      await dispatch(
        billActions.getLatestBills({
          meta: {
            page: billsPage - 1,
            count: billsPageSize,
          },
        })
      ).unwrap();

      onClose();
    } catch (e: any) {
      console.log("Failed to generate PDF, ", e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      className={`btn btn-primary border-0`}
      onClick={onSubmit}
      loading={loading}
    >
      {editMode ? "Update" : "Create and Download PDF"}
    </Button>
  );
};
